/**
 * Token Parser — Pure-function tokenizer for inline widgets.
 *
 * Framework-agnostic: zero Vue/Tiptap/DOM dependencies.
 * Parses mention tokens (@[Label](model:id)) and generic widget tokens ({{type:params}}).
 *
 * @public
 * @useWhen Parsing text for inline widget tokens; pre-extracting tokens before markdown;
 *          serializing tokens back to text; stripping tokens for plain text
 */

import type { ParsedToken, PreExtractResult } from './types'

// ============================================================================
// Regex Patterns
// ============================================================================

/** Matches @[Display Name](model:id) */
const MENTION_PATTERN = /@\[([^\]]+)\]\(([a-z_-]+):([a-zA-Z0-9_-]+)\)/g

/** Matches {{widget-type:param1:param2}} or {{widget-type}} */
const WIDGET_PATTERN = /\{\{([a-z][a-z0-9_-]*)(?::([^}]+))?\}\}/g

/** Placeholder prefix used during pre-extraction (avoid __ which markdown renders as bold) */
const PLACEHOLDER_PREFIX = 'XWIDGETX'
const PLACEHOLDER_SUFFIX = 'XENDX'

// ============================================================================
// Core Parsing
// ============================================================================

/**
 * Parse all tokens (mentions + widgets) from text.
 * Returns tokens sorted by their start position.
 */
export function parseTokens(text: string): ParsedToken[] {
  if (!text) return []

  const tokens: ParsedToken[] = []

  // Find all mentions
  const mentionRegex = new RegExp(MENTION_PATTERN.source, 'g')
  let match: RegExpExecArray | null
  while ((match = mentionRegex.exec(text)) !== null) {
    tokens.push({
      type: 'mention',
      raw: match[0],
      label: match[1]!,
      model: match[2]!,
      id: match[3]!,
      start: match.index,
      end: match.index + match[0].length,
    })
  }

  // Find all generic widgets
  const widgetRegex = new RegExp(WIDGET_PATTERN.source, 'g')
  while ((match = widgetRegex.exec(text)) !== null) {
    const paramsStr = match[2] ?? ''
    tokens.push({
      type: 'widget',
      raw: match[0],
      widgetType: match[1]!,
      params: paramsStr ? paramsStr.split(':') : [],
      start: match.index,
      end: match.index + match[0].length,
    })
  }

  // Sort by position, remove overlaps (first match wins)
  tokens.sort((a, b) => a.start - b.start)

  const filtered: ParsedToken[] = []
  let lastEnd = 0
  for (const token of tokens) {
    if (token.start >= lastEnd) {
      filtered.push(token)
      lastEnd = token.end
    }
  }

  return filtered
}

/**
 * Split text into alternating strings and parsed tokens.
 * Text segments between tokens are plain strings.
 */
export function splitByTokens(text: string): Array<string | ParsedToken> {
  if (!text) return []

  const tokens = parseTokens(text)
  if (tokens.length === 0) return [text]

  const result: Array<string | ParsedToken> = []
  let cursor = 0

  for (const token of tokens) {
    // Add text before this token
    if (token.start > cursor) {
      result.push(text.slice(cursor, token.start))
    }
    result.push(token)
    cursor = token.end
  }

  // Add trailing text
  if (cursor < text.length) {
    result.push(text.slice(cursor))
  }

  return result
}

/**
 * Pre-extract tokens from text, replacing them with unique placeholders.
 * This makes the text safe for markdown parsing (tokens won't be mangled).
 *
 * After markdown parsing, use the returned map to replace placeholders
 * with Vue components.
 */
export function preExtractTokens(text: string): PreExtractResult {
  if (!text) return { cleaned: '', tokens: new Map() }

  const tokens = parseTokens(text)
  if (tokens.length === 0) return { cleaned: text, tokens: new Map() }

  const tokenMap = new Map<string, ParsedToken>()
  let cleaned = ''
  let cursor = 0

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]!
    const placeholder = `${PLACEHOLDER_PREFIX}${i}${PLACEHOLDER_SUFFIX}`

    // Add text before this token
    cleaned += text.slice(cursor, token.start)
    // Add placeholder
    cleaned += placeholder
    tokenMap.set(placeholder, token)

    cursor = token.end
  }

  // Add trailing text
  cleaned += text.slice(cursor)

  return { cleaned, tokens: tokenMap }
}

/**
 * Split an HTML string by widget placeholders.
 * Returns alternating HTML strings and placeholder keys.
 */
export function splitByPlaceholders(
  html: string,
  tokenMap: Map<string, ParsedToken>,
): Array<{ type: 'html'; html: string } | { type: 'placeholder'; key: string; token: ParsedToken }> {
  if (tokenMap.size === 0) return [{ type: 'html', html }]

  // Build a regex that matches any placeholder
  const placeholderKeys = Array.from(tokenMap.keys())
  const escapedKeys = placeholderKeys.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const pattern = new RegExp(`(${escapedKeys.join('|')})`, 'g')

  const parts = html.split(pattern)
  const result: Array<{ type: 'html'; html: string } | { type: 'placeholder'; key: string; token: ParsedToken }> = []

  for (const part of parts) {
    if (!part) continue

    const token = tokenMap.get(part)
    if (token) {
      result.push({ type: 'placeholder', key: part, token })
    } else {
      result.push({ type: 'html', html: part })
    }
  }

  return result
}

// ============================================================================
// Serialization
// ============================================================================

/** Serialize a mention back to token syntax: @[label](model:id) */
export function serializeMention(label: string, model: string, id: string): string {
  return `@[${label}](${model}:${id})`
}

/** Serialize a widget back to token syntax: {{type:p1:p2}} */
export function serializeWidget(widgetType: string, params: string[]): string {
  if (params.length === 0) return `{{${widgetType}}}`
  return `{{${widgetType}:${params.join(':')}}}`
}

// ============================================================================
// Plain Text Conversion
// ============================================================================

/**
 * Strip all tokens from text, replacing mentions with their labels
 * and removing widget tokens entirely. Useful for notifications,
 * search indexing, and plain-text fallbacks.
 */
export function stripTokens(text: string): string {
  if (!text) return ''

  // Replace mentions with just the label
  let result = text.replace(
    new RegExp(MENTION_PATTERN.source, 'g'),
    (_match, label: string) => label,
  )

  // Remove widget tokens
  result = result.replace(new RegExp(WIDGET_PATTERN.source, 'g'), '')

  return result.trim()
}
